<template>
  <div>
    <!-- Header Section -->
    <div class="row mb-4">
      <div class="col-md-8">
        <div class="d-flex align-items-center">
          <div class="bg-primary-light p-3 rounded-2 me-3">
            <vue-feather type="users" class="d-flex justify-content-center align-content-center" stroke="#652aa3" size="24"></vue-feather>
          </div>
          <h3 class="mb-0 fw-bold">Leave Approvers</h3>
        </div>
      </div>
      <div class="col-md-4">
        <div class="d-flex justify-content-end">
          <button
            v-if="profile.role !== 'user'"
            class="btn btn-primary shadow-sm"
            @click="openAddModal"
          >
            <vue-feather type="plus" size="18" class="me-1"></vue-feather>
            Add Leave Approver
          </button>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <Card cardClasses="shadow-sm border-0 h-100 mb-4">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <div class="font-size-125 color-gray-600">
              <vue-feather type="users" stroke="#652aa3" size="18" class="me-1"></vue-feather>
              Leave Approvers ({{approvers.length}})
            </div>
            <UInput v-model="dataTableSearch" size="md" placeholder="Search leave approver" class="mb-0 w-25 search-input"/>
          </div>

          <!-- Loading Spinner -->
          <div v-if="loading" class="text-center py-4">
            <Spinner spinner-class="mb-1" />
          </div>

          <!-- Leave Approvers by Level -->
          <div v-if="!loading && approvers.length > 0">
            <div v-for="(group, level) in approversByLevel" :key="level" class="mb-5">
              <h5 class="mb-3">
                <span>Level {{ level }} </span>
                <span class="fs-6 text-muted fst-italic">{{ "(" + getLevelDescription(Number(level)) + ")" }}</span>
                <span class="badge bg-primary ms-2">
                  {{ group.length }}

                </span>
              </h5>

              <div v-if="group.length > 0" class="table-responsive mb-4">
                <table class="table table-hover align-middle">
                  <thead class="table-light">
                    <tr>
                      <th scope="col" class="fw-semibold">First Name</th>
                      <th scope="col" class="fw-semibold">Last Name</th>
                      <th scope="col" class="fw-semibold">Email</th>
                      <th scope="col" class="fw-semibold">Work Station</th>
                      <th scope="col" class="fw-semibold">Department</th>
                      <th scope="col" class="fw-semibold">Designation</th>
                      <th v-if="profile.role !== 'user'" scope="col" class="fw-semibold text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="approver in group" :key="approver.id">
                      <td>{{ approver.user?.first_name || 'N/A' }}</td>
                      <td>{{ approver.user?.last_name || 'N/A' }}</td>
                      <td>{{ approver.user?.email || 'N/A' }}</td>
                      <td>{{ approver.work_station?.name || 'All Work Stations' }}</td>
                      <td>{{ approver.department?.name || 'All Departments' }}</td>
                      <td>{{ approver.designation?.name || 'All Designations' }}</td>
                      <td v-if="profile.role !== 'user'" class="text-end">
                        <button
                          class="btn btn-sm btn-outline-danger"
                          @click="confirmDelete(approver)"
                        >
                          <vue-feather type="trash-2" size="14"></vue-feather>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-else class="text-muted text-center py-3">
                <vue-feather type="users" size="28" class="mb-2" stroke="#652aa3"></vue-feather>
                <h6>No approvers in this level</h6>
                <button class="btn btn-sm btn-primary" @click="openAddModal">
                  <vue-feather type="plus" size="12" class="me-1"></vue-feather>
                  Add Leave Approver
                </button>
              </div>
            </div>
          </div>

          <!-- Empty State -->
          <div v-if="!loading && approvers.length === 0" class="text-center py-5">
            <vue-feather type="users" size="48" class="mb-3" stroke="#652aa3"></vue-feather>
            <h5>No Leave Approvers Found</h5>
            <p class="text-muted">Create your first leave approver to get started.</p>
            <button class="btn btn-primary" @click="openAddModal">
              <vue-feather type="plus" size="16" class="me-1"></vue-feather>
              Add Leave Approver
            </button>
          </div>
        </Card>
      </div>
    </div>

    <!-- Add Leave Approver Modal -->
    <Modal
      :open="showModal"
      @closed="closeModal"
    >
      <div class="modal-header border-0">
        <h5 class="modal-title m-0 d-flex align-items-center">
          Add Leave Approver
        </h5>
        <button @click="closeModal" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="saveApprover">
          <div class="mb-3">
            <label for="name" class="form-label">User<span class="text-danger">*</span></label>
            <select class="form-select" id="name" v-model="user.user_id" required data="">
              <option value="" disabled>Select User</option>
              <option v-for="user in users" :key="user.key" :value="user.id">
                {{ user.first_name }} {{ user.last_name }}
              </option>
            </select>
          </div>

          <!-- Workstation Selector -->
          <div class="mb-3">
            <label for="work_station" class="form-label">Workstation<span class="text-danger">*</span></label>
            <select class="form-select" id="work_station" v-model="user.work_station_id" data="">
              <option value="" disabled>All Workstations</option>
              <option v-for="work_station in work_stations" :key="'ws-'+work_station.id" :value="work_station.id">
                {{ work_station.name }}
              </option>
            </select>
          </div>

          <!-- Department Selector -->
          <div class="mb-3">
            <label for="department" class="form-label">Department<span class="text-danger">*</span></label>
            <select class="form-select" id="department" v-model="user.department_id" data="">
              <option value="" disabled>All Departments</option>
              <option v-for="dept in departments" :key="'dept-'+dept.id" :value="dept.id">
                {{ dept.name }}
              </option>
            </select>
          </div>

          <!-- Designation Selector -->
          <div class="mb-3">
            <label for="designation" class="form-label">Designation<span class="text-danger">*</span></label>
            <select class="form-select" id="designation" v-model="user.designation_id" data="">
              <option value="" disabled>All Designations</option>
              <option v-for="designation in designations" :key="'desig-'+designation.id" :value="designation.id">
                {{ designation.name }}
              </option>
            </select>
          </div>

          <div class="mb-3">
            <label for="level" class="form-label">Level<span class="text-danger">*</span></label>
            <select class="form-select" id="level" v-model="user.level" required data="">
              <option value="" disabled>Select Level</option>
              <option v-for="lvl in [1,2,3,4]" :key="lvl" :value="lvl">Level {{ lvl }} ({{ getLevelDescription(lvl) }})</option>
            </select>
          </div>
          <div class="d-flex justify-content-end pt-3">
            <button type="button" class="btn btn-outline-secondary me-2" @click="closeModal">Cancel</button>
            <button type="submit" class="btn btn-primary" :disabled="saving">
              <span v-if="saving" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
              Create
            </button>
          </div>
        </form>
      </div>
    </Modal>
  </div>
</template>

<script>
import leaveService from '@/api/leaveService';
import {decrypt} from '@/utils/crypto';
import notify from '@/utils/notify';
import VueFeather from 'vue-feather';
import confirmDialog from "@/utils/confirmDialog";
import formatDate from "@/utils/formatDate";
import userService from "@/api/userService";
import departmentService from "@/api/departmentService";
import designationService from "@/api/designationService";
import stationService from "@/api/stationService";

export default {
  name: 'LeaveApprovers',
  components: { VueFeather },
  data() {
    return {
      loading: true,
      saving: false,
      users: [],
      departments: [],
      designations: [],
      work_stations: [],
      approvers: [],
      dataTableSearch: '',
      showModal: false,
      selectedApprover: null,
      profile: {},
      user: {
        user_id: '',
        department_id: '',
        designation_id: '',
        work_station_id: '',
        level: ''
      }
    };
  },
  computed: {
    approversByLevel() {
      const groups = { 1: [], 2: [], 3: [], 4: [], };
      this.approvers.forEach(approver => {
        const level = approver?.level || approver?.pivot?.level || approver?.meta?.level;
        if ([1, 2, 3, 4].includes(Number(level))) {
          groups[level].push(approver);
        }
      });
      return groups;
    },
  },
  methods: {
    confirmDialog,
    formatDate,

    async getDesignations() {
      try {
        this.designations = await designationService.getDesignations();
      } catch (error) {
        console.error('Error fetching designations:', error);
        notify.alert.error('Failed to load designations');
      }
    },
    async getWorkstations() {
      try {
        this.work_stations = await stationService.getStations();
      } catch (error) {
        console.error('Error fetching work_stations:', error);
        notify.alert.error('Failed to load work_stations');
      }
    },
    async fetchApprovers() {
      try {
        this.loading = true;
        this.approvers = await leaveService.getLeaveApprovers();
      } catch (error) {
        notify.alert.error('Failed to fetch leave approvers');
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    getLevelDescription(level) {
      switch (level) {
        case 1:
          return 'Last approvers';
        case 2:
          return 'Third approvers';
        case 3:
          return 'Second approvers';
        case 4:
          return 'First approvers';
        default:
          return 'Unknown';
      }
    },
    openAddModal() {
      this.user = {
        user_id: '',
        department_id: '',
        designation_id: '',
        work_station_id: '',
        level: ''
      };
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
    },
    async saveApprover() {
      try {
        this.saving = true;
          await leaveService.createLeaveApprover(this.user);
        this.closeModal();
        await this.fetchApprovers();
      } catch (error) {
        notify.alert.error(error.response?.data?.message || 'Failed to save leave approver');
        console.error(error);
      } finally {
        this.saving = false;
      }
    },
    confirmDelete(approver) {
      this.selectedApprover = approver;
      confirmDialog('Delete Leave Approver', () => this.deleteApprover(), 'Are you sure you want to delete this leave approver');
    },
    async deleteApprover() {
      try {
        await leaveService.deleteLeaveApprover(this.selectedApprover.id);
        await this.fetchApprovers();
      } catch (error) {
        notify.alert.error(error.response?.data?.message || 'Failed to delete leave approver');
        console.error(error);
      }
    },
    async getUsers() {
      try {
        this.users = await userService.getUsers()
      } catch (error) {
        notify.alert.error('Failed to fetch users');
        console.error(error);
      }
    },
    async getDepartments() {
      try {
        this.departments = await departmentService.getDepartments();
      } catch (error) {
        notify.alert.error('Failed to fetch departments');
        console.error(error);
      }
    },
  },
  async created() {
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')));
    await this.getUsers();
    await this.getDepartments();
    await this.getDesignations();
    await this.getWorkstations();
    await this.fetchApprovers();
  }
};
</script>
